import type { SegmentId } from '../segment/segment-id';
import { AccessibilityPrefs, mergeAccessibility } from './accessibility-prefs';
import { BrandProductsPrefs, mergeBrandProducts } from './brand-products-prefs';
import {
  CompareChartByKey,
  CompareChartPrefs,
  mergeCompareChart,
  mergeCompareChartByKey,
} from './compare-chart-prefs';
import { mergePinnedComparisons, mergeRecentComparisons } from './comparison-shortcuts-prefs';
import { mergePriceMonitor, PriceMonitorPrefs } from './price-monitor-prefs';

export interface MemberPreferencesDocument {
  brandProducts?: BrandProductsPrefs;
  accessibility?: AccessibilityPrefs;
  /** Comparison chart controls shared by every comparison. */
  compareChart?: CompareChartPrefs;
  /** Per-comparison chart state keyed by the comparison URL key (pbKey). */
  compareChartByKey?: CompareChartByKey;
  /** Comparison ids the member opened, newest first, at most five. */
  recentComparisons?: readonly SegmentId[];
  /** The comparison ids the member pinned, distinct, in pin order. */
  pinnedComparisons?: readonly SegmentId[];
  /** Price monitoring range, channels and per-brand products. */
  priceMonitor?: PriceMonitorPrefs;
}

export function parseMemberPreferencesDocument(raw: unknown): MemberPreferencesDocument {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const obj = raw as Record<string, unknown>;
  const out: MemberPreferencesDocument = {};
  if ('brandProducts' in obj) out.brandProducts = mergeBrandProducts(obj['brandProducts']);
  if ('accessibility' in obj) out.accessibility = mergeAccessibility(obj['accessibility']);
  if ('compareChart' in obj) out.compareChart = mergeCompareChart(obj['compareChart']);
  if ('compareChartByKey' in obj) {
    out.compareChartByKey = mergeCompareChartByKey(obj['compareChartByKey']);
  }
  if ('recentComparisons' in obj) {
    const recent = mergeRecentComparisons(obj['recentComparisons']);
    if (recent.length > 0) out.recentComparisons = recent;
  }
  if ('pinnedComparisons' in obj || 'pinnedComparison' in obj) {
    const pinned = mergePinnedComparisons(obj['pinnedComparisons'], obj['pinnedComparison']);
    if (pinned.length > 0) out.pinnedComparisons = pinned;
  }
  if ('priceMonitor' in obj) out.priceMonitor = mergePriceMonitor(obj['priceMonitor']);
  return out;
}
